import { Checkbox, Radio, DatePicker, Input, Select, InputNumber } from 'antd';
import * as React from 'react';
import { DocumentModel } from 'src/hooks/form/useDocument';
import { FieldModel } from 'src/hooks/form/useField';
import SEFieldValue from '../SECommon/SEFieldValue';
import withStyles from 'react-jss';
import { KeyLabel, StylesProps } from 'src/Library/Types';
import { DropdownMasterValues } from 'src/dto/response/getTaskDetails';
import SEFieldDocument from './SEFieldDocument';
import { isValidArray } from 'src/Library/Helper';

interface SEFieldInputProps extends StylesProps<ReturnType<typeof styles>> {
  field?: FieldModel<any> | DocumentModel<any>;
  dropdownMasterValues: DropdownMasterValues;
  displayNumber?: number;
  children?: any;
  [key: string]: any;
}

const SEFieldInput: React.FC<SEFieldInputProps> = ({
  field,
  // classes,
  dropdownMasterValues,
  displayNumber,
  ...props
}) => {
  const renderValue = () => {
    if ((field?.fieldType === 'select') && field?.extraData?.dropdownMasterId) {
      const id = isValidArray(field?.extraData?.values) && field?.extraData?.values.length === 1 && field?.extraData?.values[0] || props.value;
      const options = dropdownMasterValues?.[field?.extraData?.dropdownMasterId];
      let value = options?.find((el) => el.id === id)?.value;

      /** Searching by value for type DROPDOWN */
      if (field?.extraData?.type === 'DROPDOWN')
        value = options?.find(el => el.value === id)?.value;

      return <SEFieldValue {...props} value={value} displayNumber={displayNumber} />;
    }
    if (field?.fieldType === 'multiSelect') {
      return <SEFieldValue {...props} value={field?.extraData?.values} displayNumber={displayNumber} />;
    }
    if (field?.fieldType === 'document') {
      return <SEFieldDocument {...props} />;
    }
    if (field?.fieldType === 'checkbox') {
      return <Checkbox checked={props.value === 'true' || props.value === true} disabled />;
    }
    return <SEFieldValue {...props} displayNumber={displayNumber} />;
  };

  if (field?.disabled) return <div style={{ lineHeight: 'normal' }}>{renderValue()}</div>;

  const renderWithNumber = (child: JSX.Element) => {
    if (displayNumber === undefined) return child;
    return (
        <div style={{ display: 'flex', flexDirection:'row', alignItems: 'center', gap: 4, width:'100%' }}>
          <div>{displayNumber}.</div>
          <div style={{ width:'100%' }}>{child}</div>
        </div>
    )
  }

  const style = { ...props.style };
  switch (field?.fieldType) {
    case 'radio':
      // return <Radio.Group/>
      return renderWithNumber(
        <Radio.Group
          {...props}
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '8px',
          }}
        />
      );
    case 'document':
      return <SEFieldDocument {...props} />;
    case 'date':
      return renderWithNumber(
        <DatePicker
          showTime
          {...props}
          popupStyle={{ zIndex: 1060 }}
          style={{ width: '100%', ...style, minWidth: 0 }}
          format="DD/MM/YYYY | hh:mm A"
        />
      );
    case 'checkbox': {
      /** Persisted value is the string "true" / "false"; convert to/from the boolean Checkbox needs.
       *  `onCheckboxToggle` is an optional extra callback (used for SMT70 conditional rendering) — it
       *  is kept separate from `onChange` so the form binding injected by getFieldDecorator is preserved. */
      const { value, onChange, onCheckboxToggle, ...checkboxProps } = props;
      return renderWithNumber(
        <Checkbox
          {...checkboxProps}
          checked={value === 'true' || value === true}
          onChange={(e) => {
            const { checked } = e.target;
            onChange && onChange(checked ? 'true' : 'false');
            onCheckboxToggle && onCheckboxToggle(checked);
          }}
        />
      );
    }
    case 'input':
      return renderWithNumber(<Input {...props} />);
    case 'inputNumber':
      delete props.style;
      return renderWithNumber(<InputNumber type="number" {...props} min={0} style={{ width: '100%', ...style }} />);
    case 'select': {
      delete props.style;
      let getValue = (el :{id: string; value: string;}) => el?.id;

      /** Searching by value for type DROPDOWN */
      if (field?.extraData?.type === 'DROPDOWN') getValue = (el :{id: string; value: string;}) => el?.value;

      return renderWithNumber(dropdownMasterValues && field?.extraData?.dropdownMasterId ? (
        <Select allowClear showSearch style={{ width: '100%', ...style }} dropdownMatchSelectWidth={false} {...props}>
          {dropdownMasterValues[field?.extraData?.dropdownMasterId]?.map((el) => {
            return (
              <Select.Option key={el.id} value={getValue(el)}>
                {el.value}
              </Select.Option>
            );
          })}
        </Select>
      ) : (
        <Select allowClear showSearch style={{ maxWidth: 160, width: '100%', ...style }} dropdownMatchSelectWidth={false}
         {...props} />
      ));
    }
    case 'multiSelect':
      delete props.style;
      return renderWithNumber(<Select mode='multiple' optionFilterProp={'label'} allowClear showSearch dropdownMatchSelectWidth={false}
      style={{ width: '100%', ...style }} {...props} />)
    default:
      return <div style={{ lineHeight: 'normal' }}>{renderValue()}</div>
  }
};

const styles = (theme) => ({
  dragnDropText: {
    fontSize: '12px',
    color: '#525252',
    marginBottom: 5,
  },
  uploadStyle: {
    // width: '15%',
    // color: field.disabled ? '#D9D9D9' : '#1890FF',
    // cursor: field.disabled ? 'auto' : 'pointer',
    marginTop: '16px',
    height: '180px !important',
    '& .ant-upload': {
      display: 'flex !important',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .ant-upload-drag-container': {
      display: 'flex !important',
      flexDirection: 'column !important',
    },
  },
});

export default React.memo(withStyles(styles)(SEFieldInput));
